import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useGroups } from '../../../hooks/admin/useGroups';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useTableUrlState } from '../../../hooks/useTableUrlState';
import GroupTable from '../../../components/admin/GroupTable';
import Pagination from '../../../components/admin/Pagination';
import SearchInput from '../../../components/admin/SearchInput';

export default function GroupList() {
  const { data: groups, isLoading } = useGroups();
  // 25, not the shared default of 10: there are only a dozen or so groups, and
  // paginating them hid a newly created one on page 2.
  const { page, pageSize, search, setPage, setPageSize, setSearch } = useTableUrlState({
    defaultPageSize: 25,
  });

  const debouncedSearch = useDebouncedValue(search, 200);

  const filteredGroups = useMemo(() => {
    const list = groups ?? [];
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter((g) =>
      g.locationName.toLowerCase().includes(q) ||
      (g.locationCode ?? '').toLowerCase().includes(q) ||
      (g.description ?? '').toLowerCase().includes(q),
    );
  }, [groups, debouncedSearch]);

  const totalCount = filteredGroups.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    if (!groups) return;
    if (page > totalPages) setPage(totalPages);
  }, [groups, page, totalPages, setPage]);

  const pagedGroups = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredGroups.slice(start, start + pageSize);
  }, [filteredGroups, page, pageSize]);

  if (isLoading) return <div>Loading groups...</div>;
  if (!groups) return null;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Groups</h1>

      <div className="mb-4 flex items-center gap-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name, location, or description..."
        />
        <div className="text-gray-600 whitespace-nowrap">
          {totalCount} group{totalCount === 1 ? '' : 's'}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <GroupTable groups={pagedGroups} />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          totalItems={totalCount}
        />
      </div>

      <div className="mt-6 flex gap-4">
        <Link
          to="/admin/groups/new"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Create New Group
        </Link>
      </div>
    </div>
  );
}
